import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  BarChart3, TrendingUp, TrendingDown, Users, ShoppingCart, Calendar,
  Loader2, DollarSign, Package, CheckCircle2, Percent, Activity,
  ArrowUpRight, ArrowDownRight, Minus, CreditCard, Clock, Target,
} from "lucide-react";

import ApexChart from "react-apexcharts";

const f = (v) => new Intl.NumberFormat("vi-VN").format(v);
const fVND = (v) => v >= 1e9 ? `${(v/1e9).toFixed(1)}B` : v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : `${v}`;
const fFull = (v) => f(v) + "đ";
const CHART_COLORS = ["#605dff","#37d80a","#3584fc","#fd7e14","#e74c3c","#a855f7","#06b6d4","#f59e0b","#ec4899","#8b5cf6"];

const TABS = [
  { key: "revenue", label: "Doanh thu", icon: DollarSign },
  { key: "pos", label: "POS", icon: ShoppingCart },
  { key: "booking", label: "Booking", icon: Calendar },
  { key: "checkin", label: "Check-in", icon: CheckCircle2 },
  { key: "products", label: "Sản phẩm", icon: Package },
  { key: "customers", label: "Khách hàng", icon: Users },
  { key: "pnl", label: "Lãi/Lỗ", icon: Percent },
];

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState("revenue");

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/dashboard?days=${days}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="flex justify-center items-center h-96"><Loader2 className="w-8 h-8 text-lime-400 animate-spin" /></div>;
  if (!data) return <p className="text-slate-500 text-center py-20">Không tải được dữ liệu</p>;

  const k = data.kpi || {};
  return (
    <div className="space-y-5" data-testid="analytics-page">
      {/* Header + Filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#F8FAFC]">Thống kê</h1>
          <p className="text-xs text-slate-500">{data.period}</p>
        </div>
        <div className="flex gap-1.5">
          {[{v:1,l:"Hôm nay"},{v:7,l:"7 ngày"},{v:14,l:"14 ngày"},{v:30,l:"30 ngày"}].map(d => (
            <button key={d.v} onClick={() => setDays(d.v)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${days === d.v ? "bg-[#605dff] text-white" : "bg-[#1e293b] text-slate-400 hover:text-white"}`}>{d.l}</button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard icon={DollarSign} label="Tổng doanh thu" value={fVND(k.total_revenue||0)} change={k.revenue_change} color="#605dff" />
        <KpiCard icon={ShoppingCart} label="Tổng đơn hàng" value={f(k.total_orders||0)} change={k.orders_change} color="#3584fc" />
        <KpiCard icon={Users} label="Khách hàng" value={f(k.total_customers||0)} change={null} color="#37d80a" />
        <KpiCard icon={Target} label="Lấp sân" value={`${k.occupancy||0}%`} change={null} color="#fd7e14" />
        <KpiCard icon={Calendar} label="Booking" value={f(k.total_bookings||0)} change={null} color="#a855f7" />
        <KpiCard icon={Activity} label="Lợi nhuận gộp" value={`${k.gross_profit < 0 ? "-" : ""}${fVND(Math.abs(k.gross_profit||0))}`} change={null} color={k.gross_profit >= 0 ? "#37d80a" : "#e74c3c"} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-[#334155] pb-px">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`analytics-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition ${tab === t.key ? "border-[#605dff] text-[#605dff]" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "revenue" && <RevenueSection d={data} />}
      {tab === "pos" && <POSSection d={data} />}
      {tab === "booking" && <BookingSection d={data} />}
      {tab === "checkin" && <CheckinSection d={data} />}
      {tab === "products" && <ProductsSection d={data} />}
      {tab === "customers" && <CustomersSection d={data} />}
      {tab === "pnl" && <PnLSection d={data} />}
    </div>
  );
}

// ==================== KPI CARD ====================
function KpiCard({ icon: I, label, value, change, color }) {
  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${color}15` }}>
          <I className="w-5 h-5" style={{ color }} />
        </div>
        {change !== null && change !== undefined && (
          <span className={`flex items-center gap-0.5 text-[10px] font-medium px-2 py-0.5 rounded-full ${change >= 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
            {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {change > 0 ? "+" : ""}{change}%
          </span>
        )}
      </div>
      <p className="text-xl font-bold text-slate-100">{value}</p>
      <p className="text-[10px] text-slate-500">{label}</p>
    </div>
  );
}

// ==================== CHART CARD WRAPPER ====================
function CC({ title, children, className = "" }) {
  return (
    <div className={`bg-[#1e293b] border border-[#334155] rounded-xl p-4 ${className}`}>
      <p className="text-xs font-semibold text-slate-300 mb-3">{title}</p>
      {children}
    </div>
  );
}

// Shared ApexChart defaults (auto-detects theme from DOM)
const axOpt = (cats, yFmt) => {
  const isDark = document.documentElement.getAttribute("data-theme") !== "light";
  return {
    chart: { toolbar: { show: false }, animations: { enabled: true, speed: 600 }, foreColor: isDark ? "#94a3b8" : "#475569" },
    xaxis: { categories: cats, labels: { style: { fontSize: "10px", colors: isDark ? "#94a3b8" : "#64748b" } }, axisBorder: { color: isDark ? "#475569" : "#cbd5e1" }, axisTicks: { color: isDark ? "#475569" : "#cbd5e1" } },
    yaxis: { labels: { formatter: yFmt || (v => fVND(v)), style: { fontSize: "10px", colors: [isDark ? "#94a3b8" : "#64748b"] } } },
    grid: { borderColor: isDark ? "#475569" : "#e2e8f0", strokeDashArray: 3 },
    legend: { position: "bottom", fontSize: "10px", labels: { colors: isDark ? "#94a3b8" : "#475569" } },
    tooltip: { theme: isDark ? "dark" : "light", style: { fontSize: "11px" }, y: { formatter: yFmt || (v => fFull(v)) } },
    dataLabels: { enabled: false },
    stroke: { width: 2.5, curve: "smooth" },
    markers: { size: 3 },
  };
};

// ==================== REVENUE SECTION ====================
function RevenueSection({ d }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CC title="Doanh thu theo thời gian (so sánh kỳ trước)">
          <ApexChart type="area" height={240} options={{
            ...axOpt(d.revenue_over_time?.map(r => r.date?.slice(5)) || []),
            stroke: { curve: "smooth", width: [2.5, 1.5], dashArray: [0, 5] },
            fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.05 } },
            colors: ["#605dff", "#94a3b8"],
          }} series={[
            { name: "Hiện tại", data: d.revenue_over_time?.map(r => r.current) || [] },
            { name: "Kỳ trước", data: d.revenue_over_time?.map(r => r.previous) || [] },
          ]} />
        </CC>
        <CC title="Doanh thu theo danh mục">
          <ApexChart type="bar" height={240} options={{
            ...axOpt(d.rev_by_category?.map(r => r.category) || []),
            plotOptions: { bar: { borderRadius: 4, columnWidth: "50%" } },
            colors: CHART_COLORS,
          }} series={[{ name: "Doanh thu", data: d.rev_by_category?.map(r => r.revenue) || [] }]} />
        </CC>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CC title="Doanh thu theo giờ">
          <ApexChart type="bar" height={200} options={{
            ...axOpt(d.rev_by_hour?.map(r => r.hour) || []),
            plotOptions: { bar: { borderRadius: 3, columnWidth: "65%" } },
            colors: ["#3584fc"],
          }} series={[{ name: "Doanh thu", data: d.rev_by_hour?.map(r => r.revenue) || [] }]} />
        </CC>
        <CC title="Doanh thu theo thứ">
          <ApexChart type="bar" height={200} options={{
            ...axOpt(d.rev_by_weekday?.map(r => r.day) || []),
            plotOptions: { bar: { borderRadius: 4, columnWidth: "45%" } },
            colors: ["#605dff"],
          }} series={[{ name: "Doanh thu", data: d.rev_by_weekday?.map(r => r.revenue) || [] }]} />
        </CC>
      </div>
    </div>
  );
}

// ==================== POS SECTION ====================
function POSSection({ d }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CC title="Đơn hàng theo thời gian">
          <ApexChart type="line" height={220} options={{
            ...axOpt(d.orders_over_time?.map(r => r.date?.slice(5)) || [], v => `${v}`),
            stroke: { curve: "smooth", width: 2.5 }, colors: ["#3584fc"],
          }} series={[{ name: "Đơn hàng", data: d.orders_over_time?.map(r => r.orders) || [] }]} />
        </CC>
        <CC title="Giá trị đơn trung bình">
          <ApexChart type="area" height={220} options={{
            ...axOpt(d.avg_order_value?.map(r => r.date?.slice(5)) || []),
            stroke: { curve: "smooth", width: 2 },
            fill: { type: "gradient", gradient: { opacityFrom: 0.25, opacityTo: 0.05 } },
            colors: ["#37d80a"],
          }} series={[{ name: "AOV", data: d.avg_order_value?.map(r => r.value) || [] }]} />
        </CC>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CC title="Phương thức thanh toán">
          <ApexChart type="donut" height={220} options={{
            labels: d.payment_methods?.map(p => p.method) || [],
            colors: CHART_COLORS,
            legend: { position: "bottom", fontSize: "10px", labels: { colors: "#94a3b8" } },
            tooltip: { y: { formatter: v => fFull(v) } }, dataLabels: { enabled: false },
          }} series={d.payment_methods?.map(p => p.amount) || []} />
        </CC>
        <CC title="Trạng thái thanh toán">
          <ApexChart type="donut" height={220} options={{
            labels: d.payment_success?.map(p => p.status) || [],
            colors: ["#37d80a", "#e74c3c", "#f59e0b", "#94a3b8"],
            legend: { position: "bottom", fontSize: "10px", labels: { colors: "#94a3b8" } },
            tooltip: { y: { formatter: v => `${v} đơn` } }, dataLabels: { enabled: false },
          }} series={d.payment_success?.map(p => p.count) || []} />
        </CC>
      </div>
      <CC title="Số sản phẩm trung bình/đơn">
        <ApexChart type="line" height={180} options={{
          ...axOpt(d.avg_items?.map(r => r.date?.slice(5)) || [], v => `${v}`),
          stroke: { curve: "smooth", width: 2 }, colors: ["#a855f7"],
        }} series={[{ name: "SP/đơn", data: d.avg_items?.map(r => r.value) || [] }]} />
      </CC>
    </div>
  );
}

// ==================== BOOKING SECTION ====================
function BookingSection({ d }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CC title="Tỷ lệ lấp sân">
          <ApexChart type="area" height={220} options={{
            ...axOpt(d.occupancy_chart?.map(r => r.date?.slice(5)) || [], v => `${v}%`),
            stroke: { curve: "smooth", width: 2.5 },
            fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
            colors: ["#fd7e14"],
            tooltip: { y: { formatter: v => `${v}%` } },
          }} series={[{ name: "Lấp sân %", data: d.occupancy_chart?.map(r => r.rate) || [] }]} />
        </CC>
        <CC title="Booking theo thời gian">
          <ApexChart type="bar" height={220} options={{
            ...axOpt(d.bookings_over_time?.map(r => r.date?.slice(5)) || [], v => `${v}`),
            plotOptions: { bar: { borderRadius: 3, columnWidth: "50%" } },
            colors: ["#a855f7"],
            tooltip: { y: { formatter: v => `${v} booking` } },
          }} series={[{ name: "Bookings", data: d.bookings_over_time?.map(r => r.count) || [] }]} />
        </CC>
      </div>
      <CC title="Doanh thu theo sân">
        <ApexChart type="bar" height={200} options={{
          ...axOpt(d.rev_by_court?.map(r => r.court) || []),
          plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "55%" } },
          colors: CHART_COLORS,
        }} series={[{ name: "Doanh thu", data: d.rev_by_court?.map(r => r.revenue) || [] }]} />
      </CC>
      {/* Heatmap */}
      <CC title="Biểu đồ nhiệt Booking (7 ngày x giờ)">
        <Heatmap data={d.heatmap || []} />
      </CC>
    </div>
  );
}

// ==================== HEATMAP ====================
function Heatmap({ data }) {
  const hours = Array.from({ length: 17 }, (_, i) => i + 5);
  const maxVal = Math.max(1, ...data.flatMap(d => hours.map(h => d.hours?.[String(h)] || 0)));
  const getColor = (v) => {
    if (v === 0) return "bg-[#0f172a]";
    const ratio = v / maxVal;
    if (ratio > 0.7) return "bg-[#605dff]";
    if (ratio > 0.4) return "bg-[#605dff]/60";
    if (ratio > 0.15) return "bg-[#605dff]/30";
    return "bg-[#605dff]/15";
  };
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        <div className="flex gap-0.5 mb-1 pl-10">
          {hours.map(h => <div key={h} className="flex-1 text-center text-[8px] text-slate-600">{h}h</div>)}
        </div>
        {data.map((row, i) => (
          <div key={i} className="flex gap-0.5 mb-0.5 items-center">
            <div className="w-10 text-[9px] text-slate-500 text-right pr-1">{row.day}</div>
            {hours.map(h => {
              const v = row.hours?.[String(h)] || 0;
              return <div key={h} className={`flex-1 h-6 rounded-sm ${getColor(v)} flex items-center justify-center`}>
                {v > 0 && <span className="text-[7px] text-white/80">{v}</span>}
              </div>;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== CHECK-IN SECTION ====================
function CheckinSection({ d }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CC title="Tỷ lệ Check-in QR" className="flex flex-col items-center justify-center">
          <ApexChart type="radialBar" height={220} options={{
            plotOptions: { radialBar: { hollow: { size: "55%" }, dataLabels: { name: { show: true, fontSize: "12px", color: "#94a3b8" }, value: { fontSize: "28px", fontWeight: 700, color: "#e2e8f0", formatter: v => `${v}%` } } } },
            labels: ["Check-in"],
            colors: ["#605dff"],
          }} series={[d.checkin_rate || 0]} />
        </CC>
        <CC title="Check-in theo giờ">
          <ApexChart type="bar" height={220} options={{
            ...axOpt(d.checkin_by_hour?.map(c => c.hour) || [], v => `${v}`),
            plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
            colors: ["#37d80a"],
            tooltip: { y: { formatter: v => `${v} lượt` } },
          }} series={[{ name: "Check-in", data: d.checkin_by_hour?.map(c => c.count) || [] }]} />
        </CC>
        <CC title="Trạng thái vé">
          <ApexChart type="donut" height={220} options={{
            labels: d.ticket_status?.map(t => t.status) || [],
            colors: ["#37d80a", "#3584fc", "#f59e0b", "#e74c3c"],
            legend: { position: "bottom", fontSize: "9px", labels: { colors: "#94a3b8" } },
            tooltip: { y: { formatter: v => `${v} vé` } }, dataLabels: { enabled: false },
          }} series={d.ticket_status?.map(t => t.count) || []} />
        </CC>
      </div>
    </div>
  );
}

// ==================== PRODUCTS SECTION ====================
function ProductsSection({ d }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CC title="Top 10 sản phẩm bán chạy">
          <ApexChart type="bar" height={280} options={{
            ...axOpt(d.top_products?.map(p => p.name?.slice(0, 20)) || []),
            plotOptions: { bar: { borderRadius: 3, horizontal: true, barHeight: "55%" } },
            colors: CHART_COLORS,
          }} series={[{ name: "Doanh thu", data: d.top_products?.map(p => p.revenue) || [] }]} />
        </CC>
        <CC title="Doanh thu theo nhóm sản phẩm">
          <ApexChart type="donut" height={280} options={{
            labels: d.rev_by_prod_cat?.map(c => c.category) || [],
            colors: CHART_COLORS,
            legend: { position: "bottom", fontSize: "10px", labels: { colors: "#94a3b8" } },
            tooltip: { y: { formatter: v => fFull(v) } }, dataLabels: { enabled: false },
          }} series={d.rev_by_prod_cat?.map(c => c.revenue) || []} />
        </CC>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CC title="Biên lợi nhuận sản phẩm (%)">
          <ApexChart type="bar" height={240} options={{
            ...axOpt(d.product_margins?.map(p => p.name?.slice(0, 18)) || [], v => `${v}%`),
            plotOptions: { bar: { borderRadius: 3, horizontal: true, barHeight: "55%" } },
            colors: ["#37d80a"],
            tooltip: { y: { formatter: v => `${v}%` } },
          }} series={[{ name: "Margin", data: d.product_margins?.map(p => p.margin) || [] }]} />
        </CC>
        <CC title="Sản phẩm sắp hết hàng">
          <LowStockTable items={d.low_stock || []} />
        </CC>
      </div>
    </div>
  );
}

function LowStockTable({ items }) {
  if (items.length === 0) return <p className="text-slate-500 text-sm text-center py-8">Không có sản phẩm sắp hết</p>;
  return (
    <div className="max-h-[240px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead><tr className="border-b border-[#334155]">
          <th className="text-left py-2 px-2 text-slate-500">Sản phẩm</th>
          <th className="text-right py-2 px-2 text-slate-500">Tồn</th>
          <th className="text-center py-2 px-2 text-slate-500">Trạng thái</th>
        </tr></thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} className="border-b border-[#334155]/30">
              <td className="py-1.5 px-2 text-slate-300 truncate max-w-[160px]">{item.name}</td>
              <td className="py-1.5 px-2 text-right text-slate-400">{item.stock} {item.unit}</td>
              <td className="py-1.5 px-2 text-center">
                <div className="w-full bg-[#0f172a] rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${item.stock <= 2 ? "bg-red-500" : item.stock <= 5 ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(item.stock / item.min_stock * 100, 100)}%` }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ==================== CUSTOMERS SECTION ====================
function CustomersSection({ d }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CC title="Khách mới vs Quay lại">
          <ApexChart type="donut" height={220} options={{
            labels: d.new_vs_returning?.map(c => c.type) || [],
            colors: ["#605dff", "#37d80a"],
            legend: { position: "bottom", fontSize: "10px", labels: { colors: "#94a3b8" } },
            tooltip: { y: { formatter: v => `${f(v)} khách` } }, dataLabels: { enabled: false },
          }} series={d.new_vs_returning?.map(c => c.count) || []} />
        </CC>
        <CC title="Tăng trưởng khách hàng">
          <ApexChart type="area" height={220} options={{
            ...axOpt(d.customer_growth?.map(c => c.date?.slice(5)) || [], v => `${v}`),
            stroke: { curve: "smooth", width: 2 },
            fill: { type: "gradient", gradient: { opacityFrom: 0.25, opacityTo: 0.05 } },
            colors: ["#3584fc"],
            tooltip: { y: { formatter: v => `+${v} khách mới` } },
          }} series={[{ name: "Khách mới", data: d.customer_growth?.map(c => c.count) || [] }]} />
        </CC>
        <CC title="Phân khúc khách hàng">
          <ApexChart type="bar" height={220} options={{
            ...axOpt(d.customer_segments?.map(c => c.segment) || [], v => `${v}`),
            plotOptions: { bar: { borderRadius: 4, columnWidth: "50%" } },
            colors: CHART_COLORS,
            tooltip: { y: { formatter: v => `${f(v)} khách` } },
          }} series={[{ name: "Số lượng", data: d.customer_segments?.map(c => c.count) || [] }]} />
        </CC>
      </div>
    </div>
  );
}

// ==================== P&L SECTION ====================
function PnLSection({ d }) {
  return (
    <div className="space-y-4">
      <CC title="Lợi nhuận gộp theo thời gian">
        <ApexChart type="area" height={240} options={{
          ...axOpt(d.profit_chart?.map(p => p.date?.slice(5)) || []),
          stroke: { curve: "smooth", width: [2.5, 2, 2], dashArray: [0, 0, 4] },
          fill: { type: "gradient", gradient: { opacityFrom: 0.2, opacityTo: 0.02 } },
          colors: ["#605dff", "#e74c3c", "#37d80a"],
        }} series={[
          { name: "Doanh thu", data: d.profit_chart?.map(p => p.revenue) || [] },
          { name: "Chi phí", data: d.profit_chart?.map(p => p.cost) || [] },
          { name: "Lợi nhuận", data: d.profit_chart?.map(p => p.profit) || [] },
        ]} />
      </CC>
      {d.expense_breakdown?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CC title="Chi phí theo loại">
            <ApexChart type="donut" height={250} options={{
              labels: d.expense_breakdown?.map(e => e.category) || [],
              colors: CHART_COLORS,
              legend: { position: "bottom", fontSize: "10px", labels: { colors: "#94a3b8" } },
              tooltip: { y: { formatter: v => fFull(v) } }, dataLabels: { enabled: false },
            }} series={d.expense_breakdown?.map(e => e.amount) || []} />
          </CC>
          <CC title="Tóm tắt P&L" className="flex flex-col justify-center">
            <div className="space-y-3">
              {[
                { label: "Tổng doanh thu", value: d.profit_chart?.reduce((a, p) => a + p.revenue, 0) || 0, color: "text-[#605dff]" },
                { label: "Tổng chi phí", value: d.profit_chart?.reduce((a, p) => a + p.cost, 0) || 0, color: "text-red-400" },
                { label: "Lợi nhuận gộp", value: d.profit_chart?.reduce((a, p) => a + p.profit, 0) || 0, color: "text-emerald-400" },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#334155]/30 last:border-0">
                  <span className="text-xs text-slate-400">{item.label}</span>
                  <span className={`text-lg font-bold ${item.color}`}>{fFull(item.value)}</span>
                </div>
              ))}
            </div>
          </CC>
        </div>
      )}
    </div>
  );
}
